import React, { useState, useEffect, useRef, useCallback, useMemo, useContext } from 'react'
import ReactDOM from 'react-dom'
import styles from './ParagraphPracticeView.module.scss'
import { useMutation } from '@apollo/client'
import { RECORD_PRACTICE_MUTATION } from '../query/gqlQueries'
import UserContext from '../context/UserContext'

import ArrowBackIcon from '@mui/icons-material/ArrowBack'
import CloseIcon from '@mui/icons-material/Close'
import MicIcon from '@mui/icons-material/Mic'
import MicOffIcon from '@mui/icons-material/MicOff'
import NavigateBeforeIcon from '@mui/icons-material/NavigateBefore'
import NavigateNextIcon from '@mui/icons-material/NavigateNext'
import RefreshIcon from '@mui/icons-material/Refresh'

// A token only "requires speech" if it's a real spoken word — has at least
// two letters and no digits. Numbers, parens, abbreviations like "NR-1",
// symbols like "nº" all auto-pass so the user never gets trapped waiting on
// something STT will never transcribe.
const requiresSpeech = (word) => {
  if (!word) return false
  const letters = word.match(/\p{L}/gu) || []
  if (letters.length < 2) return false
  if (/\p{N}/u.test(word)) return false
  return true
}

// Map our ISO-3 language codes to BCP 47 locales the Web Speech API expects.
// Falls back to 'en-US' for anything missing.
const LANG_MAP = {
  eng: 'en-US',
  por: 'pt-BR',
  tur: 'tr-TR',
  kor: 'ko-KR',
  jpn: 'ja-JP',
  cmn: 'zh-CN',
  spa: 'es-ES',
  fra: 'fr-FR',
  deu: 'de-DE',
  ita: 'it-IT',
  rus: 'ru-RU',
  ara: 'ar-SA',
  ell: 'el-GR',
  nld: 'nl-NL',
  swe: 'sv-SE',
  vie: 'vi-VN',
}

// Tokenize a sentence into renderable units. Word tokens are kept as their
// surface form; punctuation/whitespace becomes pass-through tokens so we can
// reconstruct spacing without disturbing styling.
const tokenize = (text) => {
  if (!text) return []
  const re = /(\p{L}[\p{L}\p{N}'’\-]*|\p{N}+|[^\p{L}\p{N}]+)/gu
  const out = []
  let m
  while ((m = re.exec(text)) !== null) {
    const tok = m[1]
    out.push({ text: tok, isWord: /^[\p{L}\p{N}]/u.test(tok) })
  }
  return out
}

// Normalize text for comparison: lowercase, strip combining marks-friendly
// punctuation, collapse whitespace. Keeps letters (incl. CJK) and numbers.
const normalize = (s) =>
  (s || '')
    .toLowerCase()
    .replace(/[^\p{L}\p{N}\s]/gu, ' ')
    .replace(/\s+/g, ' ')
    .trim()

export default function ParagraphPracticeView({
  paragraph,
  sentences,
  words,
  onClose,
  onExit
}) {
  const { username } = useContext(UserContext)
  const [recordPractice] = useMutation(RECORD_PRACTICE_MUTATION)

  const [currentIndex, setCurrentIndex] = useState(0)
  const [recognizedWords, setRecognizedWords] = useState(new Set()) // lowercased keys of expected words confirmed by STT
  const [flashMissed, setFlashMissed] = useState(new Set()) // brief red highlight at end of sentence
  const [isListening, setIsListening] = useState(false)
  const [error, setError] = useState(null)
  const [supported, setSupported] = useState(true)
  const [completed, setCompleted] = useState(false)

  const recognitionRef = useRef(null)
  const keepListeningRef = useRef(false)
  const currentSentenceElRef = useRef(null)

  // Index Word nodes by lowercased text for quick lookup when recording practice.
  const wordsByText = useMemo(() => {
    const map = {}
    ;(words || []).forEach(pair => {
      if (pair?.source?.text) {
        map[pair.source.text.toLowerCase()] = pair
      }
    })
    return map
  }, [words])

  const currentSentence = sentences?.[currentIndex] || null
  const currentText = currentSentence?.source?.text || ''
  const tokens = useMemo(() => tokenize(currentText), [currentText])
  const expectedWords = useMemo(
    () => tokens.filter(t => t.isWord).map(t => t.text),
    [tokens]
  )

  const sourceLang = paragraph?.properties?.lang || 'eng'
  const sttLang = LANG_MAP[sourceLang] || 'en-US'

  // ── Speech recognition lifecycle ────────────────────────────────────────
  useEffect(() => {
    const Ctor = window.SpeechRecognition || window.webkitSpeechRecognition
    if (!Ctor) {
      setSupported(false)
      return
    }
    const rec = new Ctor()
    rec.lang = sttLang
    rec.continuous = true
    rec.interimResults = true
    rec.maxAlternatives = 1
    recognitionRef.current = rec
    return () => {
      try { rec.stop() } catch (e) { /* noop */ }
      recognitionRef.current = null
    }
  }, [sttLang])

  // Bind result/end/error handlers — re-bound when the current sentence
  // changes so the matching closure sees the right expected words.
  useEffect(() => {
    const rec = recognitionRef.current
    if (!rec) return

    rec.onresult = (event) => {
      let transcript = ''
      for (let i = 0; i < event.results.length; i++) {
        transcript += event.results[i][0].transcript + ' '
      }
      const normalizedTranscript = ` ${normalize(transcript)} `
      const matched = new Set()
      for (const word of expectedWords) {
        const norm = normalize(word)
        if (!norm) continue
        if (normalizedTranscript.includes(` ${norm} `)) {
          matched.add(word.toLowerCase())
        }
      }
      setRecognizedWords(matched)
    }

    rec.onerror = (event) => {
      // 'no-speech' and 'aborted' are normal; don't surface as errors.
      if (event.error !== 'no-speech' && event.error !== 'aborted') {
        setError(event.error || 'recognition error')
      }
    }

    rec.onend = () => {
      // Auto-restart if we're meant to be listening (handles browser-side
      // auto-stops on silence).
      if (keepListeningRef.current) {
        try { rec.start() } catch (e) { /* noop — already started */ }
      } else {
        setIsListening(false)
      }
    }
  }, [expectedWords])

  // ── Auto-advance when all *speakable* expected words recognized ─────────
  // Tokens that don't require speech (numbers, parens, "nº", "NR-1") auto-pass.
  useEffect(() => {
    if (expectedWords.length === 0) return
    const allMatched = expectedWords.every(w =>
      !requiresSpeech(w) || recognizedWords.has(w.toLowerCase())
    )
    if (allMatched) {
      const timeout = setTimeout(() => finishSentence(true), 1200)
      return () => clearTimeout(timeout)
    }
  // eslint-disable-next-line
  }, [recognizedWords, expectedWords])

  // ── Scroll current sentence into view (Spotify-style centering) ─────────
  useEffect(() => {
    if (currentSentenceElRef.current) {
      currentSentenceElRef.current.scrollIntoView({ behavior: 'smooth', block: 'center' })
    }
  }, [currentIndex])

  // ── Recording practice for every speakable word at completion ──────────
  // Non-speech tokens (numbers, parens, abbreviations) aren't recorded so the
  // user isn't penalized for things STT couldn't transcribe anyway.
  const recordSentenceOutcome = useCallback(() => {
    if (!username) return
    expectedWords.forEach(word => {
      if (!requiresSpeech(word)) return
      const key = word.toLowerCase()
      const pair = wordsByText[key]
      if (!pair?.source?.id) return
      const isCorrect = recognizedWords.has(key)
      recordPractice({
        variables: {
          username,
          itemId: pair.source.id,
          itemType: 'Word',
          isCorrect
        }
      }).catch(err => console.warn('recordPractice failed', err))
    })
  }, [expectedWords, recognizedWords, wordsByText, username, recordPractice])

  // ── Sentence transition ─────────────────────────────────────────────────
  const finishSentence = useCallback((auto) => {
    recordSentenceOutcome()

    // Flash unmatched words red briefly so the user sees what they missed
    const missed = new Set(
      expectedWords
        .filter(w => !recognizedWords.has(w.toLowerCase()))
        .map(w => w.toLowerCase())
    )
    setFlashMissed(missed)

    setTimeout(() => {
      setFlashMissed(new Set())
      setRecognizedWords(new Set())
      setCurrentIndex(prev => {
        const next = prev + 1
        if (next >= sentences.length) {
          setCompleted(true)
          // stop listening at the end of the paragraph
          keepListeningRef.current = false
          try { recognitionRef.current?.stop() } catch (e) { /* noop */ }
          return prev
        }
        return next
      })
    }, auto ? 900 : 600)
  }, [recordSentenceOutcome, expectedWords, recognizedWords, sentences])

  // ── Manual controls ─────────────────────────────────────────────────────
  const toggleListening = useCallback(() => {
    const rec = recognitionRef.current
    if (!rec) return
    if (isListening) {
      keepListeningRef.current = false
      try { rec.stop() } catch (e) { /* noop */ }
      setIsListening(false)
    } else {
      keepListeningRef.current = true
      setError(null)
      try {
        rec.start()
        setIsListening(true)
      } catch (e) {
        setError(e.message || 'Could not start microphone')
        setIsListening(false)
      }
    }
  }, [isListening])

  const handlePrev = useCallback(() => {
    setRecognizedWords(new Set())
    setFlashMissed(new Set())
    setCurrentIndex(prev => Math.max(0, prev - 1))
    setCompleted(false)
  }, [])

  const handleNext = useCallback(() => {
    if (currentIndex >= sentences.length - 1) return
    finishSentence(false)
  }, [currentIndex, sentences, finishSentence])

  const handleRestart = useCallback(() => {
    setRecognizedWords(new Set())
    setFlashMissed(new Set())
  }, [])

  const handleRestartParagraph = useCallback(() => {
    setRecognizedWords(new Set())
    setFlashMissed(new Set())
    setCurrentIndex(0)
    setCompleted(false)
  }, [])

  // ── Render ──────────────────────────────────────────────────────────────
  // We portal everything to document.body so any transformed/filtered
  // ancestor in the Hud tree can't trap `position: fixed` and hide the
  // header or controls bar.
  const portalTarget = typeof document !== 'undefined' ? document.body : null
  const renderPortaled = (node) => portalTarget ? ReactDOM.createPortal(node, portalTarget) : node

  if (!supported) {
    return renderPortaled(
      <div className={styles.fallback}>
        <h2>Reading practice isn't supported in this browser.</h2>
        <p>Open this app in Chrome, Edge, or Safari to use speech recognition.</p>
        <button className={styles.fallbackBtn} onClick={onClose}>← Back to reader</button>
      </div>
    )
  }

  if (!sentences || sentences.length === 0) {
    return renderPortaled(
      <div className={styles.fallback}>
        <h2>No sentences to practice.</h2>
        <p>This paragraph has no split sentences — re-import it via the Add Paragraph dialog.</p>
        <button className={styles.fallbackBtn} onClick={onClose}>← Back to reader</button>
      </div>
    )
  }

  // Counter only reflects words that actually need to be spoken — auto-pass
  // tokens (numbers, parens, etc.) aren't part of the denominator.
  const speakableWords = expectedWords.filter(requiresSpeech)
  const matchedCount = speakableWords.filter(w => recognizedWords.has(w.toLowerCase())).length
  const totalCount = speakableWords.length

  return renderPortaled(
    <div className={styles.view}>
      <header className={styles.header}>
        <button className={styles.iconBtn} onClick={onClose}>
          <ArrowBackIcon fontSize="small" /> Reader
        </button>
        <div className={styles.title}>{paragraph?.properties?.title || 'Reading practice'}</div>
        <div className={styles.headerRight}>
          <div className={styles.progress}>
            {currentIndex + 1} / {sentences.length} · {matchedCount}/{totalCount} spoken
          </div>
          {onExit && (
            <button className={styles.iconBtn} onClick={onExit} title="Close paragraph view">
              <CloseIcon fontSize="small" />
            </button>
          )}
        </div>
      </header>

      {error && (
        <div className={styles.errorBar}>
          Mic error: {error}. Try toggling the mic again, or check your browser permission.
        </div>
      )}

      {completed && (
        <div className={styles.completedBanner}>
          ✓ Paragraph complete — every spoken word has been logged to practice history.{' '}
          <button className={styles.linkBtn} onClick={handleRestartParagraph}>Restart</button>
        </div>
      )}

      {!isListening && !completed && (
        <button
          className={styles.startOverlay}
          onClick={toggleListening}
          title="Click to enable your microphone and start reading aloud">
          <span className={styles.startOverlayIcon}><MicIcon style={{ fontSize: 56 }} /></span>
          <span className={styles.startOverlayTitle}>Tap to start reading aloud</span>
          <span className={styles.startOverlaySub}>
            Your microphone will listen and mark words as you say them.
            Listening in <strong>{sttLang}</strong>.
          </span>
        </button>
      )}

      <div className={styles.scroller}>
        {sentences.map((pair, idx) => {
          const isCurrent = idx === currentIndex
          const isPast = idx < currentIndex
          const isFuture = idx > currentIndex
          const sentenceTokens = isCurrent ? tokens : tokenize(pair.source.text)

          let stateClass = ''
          if (isCurrent) stateClass = styles.sentenceCurrent
          else if (isPast) stateClass = styles.sentencePast
          else if (isFuture) stateClass = styles.sentenceFuture

          return (
            <div
              key={pair.source.id}
              ref={isCurrent ? currentSentenceElRef : null}
              className={`${styles.sentence} ${stateClass}`}>
              <div className={styles.sentenceText}>
                {sentenceTokens.map((tok, i) => {
                  if (!tok.isWord) return <span key={i}>{tok.text}</span>
                  const key = tok.text.toLowerCase()
                  const recognized = isCurrent && recognizedWords.has(key)
                  const missed = isCurrent && flashMissed.has(key)
                  const autoPass = !requiresSpeech(tok.text)
                  const cls = [styles.word]
                  if (autoPass) cls.push(styles.wordAutoPass)
                  if (recognized) cls.push(styles.wordRecognized)
                  if (missed) cls.push(styles.wordMissed)
                  return (
                    <span key={i} className={cls.join(' ')}>{tok.text}</span>
                  )
                })}
              </div>
              {isCurrent && pair.target?.text && (
                <div className={styles.translation}>{pair.target.text}</div>
              )}
            </div>
          )
        })}
      </div>

      <div className={styles.footer}>
        <div className={styles.controls}>
          <button
            className={styles.controlBtn}
            onClick={handlePrev}
            disabled={currentIndex === 0}
            title="Previous sentence">
            <NavigateBeforeIcon />
          </button>
          <button
            className={styles.controlBtn}
            onClick={handleRestart}
            title="Restart current sentence">
            <RefreshIcon />
          </button>
          <button
            className={`${styles.micBtn} ${isListening ? styles.micBtnActive : ''}`}
            onClick={toggleListening}
            title={isListening ? 'Stop listening' : 'Start listening'}>
            {isListening ? <MicIcon /> : <MicOffIcon />}
          </button>
          <button
            className={`${styles.controlBtn} ${styles.controlBtnSkip}`}
            onClick={handleNext}
            disabled={currentIndex >= sentences.length - 1}
            title="Skip / next sentence (records any unspoken words as missed)">
            <NavigateNextIcon /> Skip
          </button>
        </div>

        <div className={styles.langHint}>
          Listening in <strong>{sttLang}</strong>. Speak the highlighted sentence — green = recognized, red flash = missed. Stuck? Tap <strong>Skip</strong>.
        </div>
      </div>
    </div>
  )
}
